import { z } from 'zod';

/**
 * Returns and validates the Supabase client keys from the environment.
 *
 * Note: callers (server clients, browser clients, edge runtime) DEPEND on
 * this function throwing when env vars are missing — do not silently
 * fall back. We DO augment the error message with VERCEL_ENV and
 * VERCEL_URL to make Sentry triage of misconfigured Vercel previews
 * fast (see BAL-169).
 */
export function getSupabaseClientKeys() {
  try {
    return z
      .object({
        url: z.string({
          description: `This is the URL of your hosted Supabase instance. Please provide the variable NEXT_PUBLIC_SUPABASE_URL.`,
          required_error: `Please provide the variable NEXT_PUBLIC_SUPABASE_URL`,
        }),
        publicKey: z.string({
          description: `This is the public key provided by Supabase. It is a public key used client-side. Please provide the variable NEXT_PUBLIC_SUPABASE_PUBLIC_KEY.`,
          required_error: `Please provide the variable NEXT_PUBLIC_SUPABASE_PUBLIC_KEY`,
        }),
      })
      .parse({
        url: process.env.NEXT_PUBLIC_SUPABASE_URL,
        publicKey:
          process.env.NEXT_PUBLIC_SUPABASE_PUBLIC_KEY ||
          process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY,
      });
  } catch (err) {
    // Augment the error with deployment context so Sentry triage of
    // misconfigured Vercel previews / branch deploys is one glance instead
    // of guess-the-host. NEXT_PUBLIC_VERCEL_ENV is preferred on the
    // browser since VERCEL_ENV isn't exposed there.
    const vercelEnv =
      process.env.NEXT_PUBLIC_VERCEL_ENV ?? process.env.VERCEL_ENV ?? 'unknown';
    const vercelUrl =
      process.env.NEXT_PUBLIC_VERCEL_URL ?? process.env.VERCEL_URL ?? 'unknown';
    const context = `[env=${vercelEnv}, host=${vercelUrl}]`;

    if (err instanceof Error) {
      // Mutate in place so the original error type (ZodError) is preserved
      // — callers that branch on `instanceof ZodError` keep working.
      err.message = `${err.message} ${context}`;
      throw err;
    }

    throw new Error(
      `Failed to load Supabase client keys ${context}: ${String(err)}`,
    );
  }
}
